import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import ERPLayout from "@/components/feature/ERPLayout";
import BackToTop from "@/components/base/BackToTop";
import { clientsApi } from "@/services/api";

// ── WhatsApp helper ──────────────────────────────────────────────────────────
function getWhatsAppSettings(): { number: string; enabled: boolean; template: string } {
  // Read from localStorage cache (set by admin settings page via custom event)
  try {
    const raw = localStorage.getItem('erp_whatsapp_settings');
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return { number: '', enabled: false, template: '' };
}

function buildWhatsAppUrl(clientPhone: string, clientName: string, accessCode: string, template: string): string {
  const portalUrl = window.location.origin + '/client-portal';
  const companyName = (() => {
    try {
      const raw = localStorage.getItem('erp_company_settings');
      if (raw) return (JSON.parse(raw) as { name?: string }).name || 'Our Company';
    } catch { /* ignore */ }
    return 'Our Company';
  })();
  const msg = (template || `Hello {client_name},\n\nYour Client Portal access code is: *{access_code}*\n\nLogin at: {portal_url}\n\nRegards,\n{company_name}`)
    .replace('{client_name}', clientName)
    .replace('{access_code}', accessCode)
    .replace('{portal_url}', portalUrl)
    .replace('{company_name}', companyName);
  const cleanPhone = clientPhone.replace(/\D/g, '');
  return `https://wa.me/${cleanPhone}?text=${encodeURIComponent(msg)}`;
}

interface Client {
  id: number;
  name: string;
  company: string;
  email: string;
  phone: string;
  address?: string;
  designation?: string;
  gstin: string;
  access_code?: string;
  total_jobs?: number;
  active_jobs?: number;
  total_billed?: number;
  totalJobs?: number;
  activeJobs?: number;
  totalBilled?: number;
  status: string;
  notes?: string;
}

interface ClientFormData {
  name: string;
  company: string;
  email: string;
  phone: string;
  address: string;
  designation: string;
  gstin: string;
  notes: string;
  status: string;
}

const EMPTY_FORM: ClientFormData = {
  name: "",
  company: "",
  email: "",
  phone: "",
  address: "",
  designation: "",
  gstin: "",
  notes: "",
  status: "active",
};

interface ClientModalProps {
  client: Client | null;
  onClose: () => void;
  onSave: (data: ClientFormData) => Promise<void>;
  onResetCode?: (id: number) => Promise<void>;
  saving: boolean;
  resettingCode?: boolean;
}

function ClientModal({ client, onClose, onSave, onResetCode, saving, resettingCode }: ClientModalProps) {
  const [form, setForm] = useState<ClientFormData>(
    client
      ? {
          name: client.name,
          company: client.company,
          email: client.email,
          phone: client.phone,
          address: client.address || "",
          designation: client.designation || "",
          gstin: client.gstin,
          notes: client.notes || "",
          status: client.status?.toLowerCase() || "active",
        }
      : EMPTY_FORM
  );
  const [codeCopied, setCodeCopied] = useState(false);
  const [waSent, setWaSent] = useState(false);
  const waSettings = getWhatsAppSettings();

  const set = (field: keyof ClientFormData, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleCopyCode = () => {
    if (client?.access_code) {
      navigator.clipboard.writeText(client.access_code).catch(() => {});
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    }
  };

  const handleSendWhatsApp = () => {
    if (!client?.access_code || !client.phone) return;
    const url = buildWhatsAppUrl(client.phone, client.name, client.access_code, waSettings.template);
    window.open(url, '_blank');
    setWaSent(true);
    setTimeout(() => setWaSent(false), 3000);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
              <i className="ri-user-add-line text-xl text-amber-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">{client ? "Edit Client" : "Add New Client"}</h3>
              <p className="text-xs text-gray-500 mt-0.5">{client ? client.name : "Fill in the client details below"}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1.5 block">Full Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="e.g. Venkat Rao"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1.5 block">Company / Organisation</label>
              <input
                type="text"
                value={form.company}
                onChange={(e) => set("company", e.target.value)}
                placeholder="e.g. HMDA Projects Ltd."
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1.5 block">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                placeholder="email@example.com"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1.5 block">Phone</label>
              <input
                type="text"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                placeholder="+91 98765 43210"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1.5 block">Designation</label>
              <input
                type="text"
                value={form.designation}
                onChange={(e) => set("designation", e.target.value)}
                placeholder="e.g. Chief Engineer"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1.5 block">GSTIN</label>
              <input
                type="text"
                value={form.gstin}
                onChange={(e) => set("gstin", e.target.value.toUpperCase())}
                placeholder="36AABCH5678G1Z2"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 mb-1.5 block">Address</label>
            <textarea
              rows={2}
              value={form.address}
              onChange={(e) => set("address", e.target.value)}
              placeholder="Full address..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 resize-none"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 mb-1.5 block">Notes</label>
            <textarea
              rows={2}
              maxLength={500}
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Internal notes about this client..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 resize-none"
            />
          </div>

          {client && (
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1.5 block">Status</label>
              <div className="flex gap-2">
                {["active", "inactive"].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => set("status", s)}
                    className={`flex-1 py-2 rounded-lg text-xs font-semibold capitalize transition-all cursor-pointer whitespace-nowrap ${
                      form.status === s
                        ? s === "active"
                          ? "bg-amber-500 text-white"
                          : "bg-gray-500 text-white"
                        : "border border-gray-200 text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Portal Access Code section — only for existing clients */}
          {client && (
            <div className="pt-1">
              <label className="text-xs font-medium text-gray-600 mb-2 block">Client Portal Access Code</label>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 font-mono text-sm font-bold text-gray-900 tracking-widest text-center select-all min-h-[36px] flex items-center justify-center">
                  {client.access_code || <span className="text-gray-400 font-normal text-xs">No code assigned</span>}
                </div>
                {client.access_code && (
                  <button
                    type="button"
                    onClick={handleCopyCode}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg border transition-all cursor-pointer whitespace-nowrap flex-shrink-0 ${
                      codeCopied ? "bg-amber-500 border-amber-500 text-white" : "border-gray-200 text-gray-500 hover:border-amber-400 hover:text-amber-600"
                    }`}
                    title="Copy code"
                  >
                    <i className={`${codeCopied ? "ri-checkbox-circle-line" : "ri-clipboard-line"} text-sm`} />
                  </button>
                )}
                {onResetCode && (
                  <button
                    type="button"
                    onClick={() => onResetCode(client.id)}
                    disabled={resettingCode}
                    className="flex items-center gap-1.5 px-3 py-2 bg-amber-50 border border-amber-200 text-amber-700 rounded-lg text-xs font-semibold hover:bg-amber-100 cursor-pointer whitespace-nowrap disabled:opacity-60 flex-shrink-0 transition-all"
                    title="Generate a new access code"
                  >
                    <i className={`${resettingCode ? "ri-loader-4-line animate-spin" : "ri-refresh-line"} text-sm`} />
                    {resettingCode ? "Generating..." : "Reset Code"}
                  </button>
                )}
              </div>

              {/* WhatsApp Send Button */}
              {client.access_code && client.phone && (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={handleSendWhatsApp}
                    className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold cursor-pointer transition-all whitespace-nowrap border-2 ${
                      waSent
                        ? 'border-green-400 text-green-700'
                        : 'border-green-300 text-green-700 hover:bg-green-50'
                    }`}
                    style={{ background: waSent ? '#dcfce7' : '#f0fdf4' }}
                  >
                    <i className={`${waSent ? 'ri-checkbox-circle-line' : 'ri-whatsapp-line'} text-base`} style={{ color: '#25D366' }} />
                    {waSent ? 'WhatsApp Opened!' : 'Send Access Code via WhatsApp'}
                  </button>
                  {!client.phone && (
                    <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                      <i className="ri-alert-line" />
                      Add a phone number to enable WhatsApp sending
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-1">
                    <i className="ri-information-line" />
                    Opens WhatsApp with the access code pre-filled for {client.phone}
                  </p>
                </div>
              )}
              {client.access_code && !client.phone && (
                <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                  <i className="ri-alert-line" />
                  Add a phone number to enable WhatsApp sending
                </p>
              )}

              <p className="text-xs text-gray-400 mt-1.5">Share this code + their email with the client to access the portal.</p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-semibold cursor-pointer hover:bg-gray-50 transition-all whitespace-nowrap"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(form)}
            disabled={saving || !form.name.trim()}
            className="flex-1 py-2.5 bg-amber-600 text-white rounded-xl text-sm font-bold cursor-pointer hover:bg-amber-700 transition-all whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <i className="ri-loader-4-line animate-spin text-sm" />
                Saving...
              </>
            ) : (
              <>
                <i className="ri-save-line text-sm" />
                {client ? "Save Changes" : "Add Client"}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ClientsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "All");
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Client | null>(null);
  const [saving, setSaving] = useState(false);
  const [resettingCode, setResettingCode] = useState(false);
  const [toast, setToast] = useState("");
  const [page, setPage] = useState(Number(searchParams.get("page")) || 1);
  const [total, setTotal] = useState(0);
  const [usingMock, setUsingMock] = useState(false);
  const [apiError, setApiError] = useState("");
  const LIMIT = 20;

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3500);
  };

  const loadClients = useCallback(async () => {
    try {
      setLoading(true);
      const response = await clientsApi.getAll({
        search: search || undefined,
        status: statusFilter === "All" ? undefined : statusFilter.toLowerCase(),
        page,
        limit: LIMIT,
      });
      setClients(response.data || []);
      setTotal(response.pagination?.total || response.data?.length || 0);
      setError("");
      setUsingMock(false);
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : String(err);
      setApiError(errMsg);
      setUsingMock(true);
      setClients([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, page]);

  useEffect(() => {
    const timer = setTimeout(loadClients, 300);
    return () => clearTimeout(timer);
  }, [loadClients]);

  // Sync URL params with filter state
  useEffect(() => {
    const params: Record<string, string> = {};
    if (statusFilter !== "All") params.status = statusFilter.toLowerCase();
    if (search.trim()) params.search = search.trim();
    if (page > 1) params.page = String(page);
    setSearchParams(params, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, search, page]);

  const handleSave = async (form: ClientFormData) => {
    setSaving(true);
    try {
      if (editTarget) {
        await clientsApi.update(editTarget.id, form as unknown as Record<string, unknown>);
        setClients((prev) => prev.map((c) => (c.id === editTarget.id ? { ...c, ...form } : c)));
        showToast("Client updated successfully");
      } else {
        const created = await clientsApi.create(form as unknown as Record<string, unknown>);
        setClients((prev) => [created, ...prev]);
        setTotal((t) => t + 1);
        showToast("Client added successfully");
      }
      setModalOpen(false);
      setEditTarget(null);
    } catch {
      if (editTarget) {
        setClients((prev) => prev.map((c) => (c.id === editTarget.id ? { ...c, ...form } : c)));
        showToast("Client updated (offline mode)");
      } else {
        const tempClient: Client = { id: Date.now(), ...form, total_jobs: 0, active_jobs: 0, total_billed: 0 };
        setClients((prev) => [tempClient, ...prev]);
        showToast("Client added (offline mode)");
      }
      setModalOpen(false);
      setEditTarget(null);
    } finally {
      setSaving(false);
    }
  };

  const handleResetCode = async (clientId: number) => {
    setResettingCode(true);
    try {
      const result = await clientsApi.resetAccessCode(clientId);
      const newCode: string = result.access_code;
      // Update both the list and the editTarget so the modal shows the new code immediately
      setClients((prev) =>
        prev.map((c) => (c.id === clientId ? { ...c, access_code: newCode } : c))
      );
      setEditTarget((prev) => (prev ? { ...prev, access_code: newCode } : prev));
      showToast("New access code generated successfully");
    } catch {
      showToast("Failed to reset access code — check backend connection");
    } finally {
      setResettingCode(false);
    }
  };

  const handleDelete = async (client: Client) => {
    try {
      await clientsApi.delete(client.id);
    } catch {
      // Optimistic
    }
    setClients((prev) => prev.filter((c) => c.id !== client.id));
    setTotal((t) => t - 1);
    showToast(`${client.name} deactivated`);
  };

  const totalPages = Math.ceil(total / LIMIT);
  const getJobs = (c: Client) => c.total_jobs ?? c.totalJobs ?? 0;
  const getActiveJobs = (c: Client) => c.active_jobs ?? c.activeJobs ?? 0;
  const getBilled = (c: Client) => c.total_billed ?? c.totalBilled ?? 0;

  return (
    <ERPLayout title="Client Management">
      {toast && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2 bg-amber-600 text-white text-sm px-4 py-2.5 rounded-xl font-medium">
          <i className="ri-checkbox-circle-line" />{toast}
        </div>
      )}

      {modalOpen && (
        <ClientModal
          client={editTarget}
          onClose={() => { setModalOpen(false); setEditTarget(null); }}
          onSave={handleSave}
          onResetCode={editTarget ? handleResetCode : undefined}
          saving={saving}
          resettingCode={resettingCode}
        />
      )}

      <div className="p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Client Management</h2>
            <p className="text-sm text-gray-500 mt-0.5">{total} total clients</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadClients}
              className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-refresh-line text-sm" /></span>
              Refresh
            </button>
            <button
              onClick={() => { setEditTarget(null); setModalOpen(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></span>
              Add Client
            </button>
          </div>
        </div>

        {/* Offline / Mock Data Banner */}
        {usingMock && (
          <div className="mb-4 p-3 rounded-xl border border-amber-200 bg-amber-50 flex items-start gap-3">
            <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-error-warning-fill text-amber-500" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-amber-700">Showing mock data — backend API unreachable</p>
              <p className="text-xs text-amber-600 mt-0.5 break-all">Error: {apiError}</p>
              <p className="text-xs text-amber-600 mt-1">
                Changes you make here will NOT be saved to the database. Go to{" "}
                <Link to="/admin/database" className="underline font-semibold">Admin → Database</Link>{" "}
                and run API Diagnostics to find the issue.
              </p>
            </div>
            <button onClick={loadClients} className="flex-shrink-0 text-xs text-amber-700 font-semibold underline cursor-pointer whitespace-nowrap">
              Retry
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-100 p-4 mb-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-search-line text-gray-400 text-sm" />
            </span>
            <input
              type="text"
              placeholder="Search clients, companies, emails..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400"
            />
          </div>
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            {["All", "Active", "Inactive"].map((s) => (
              <button
                key={s}
                onClick={() => { setStatusFilter(s); setPage(1); }}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  statusFilter === s ? "bg-white text-amber-700" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>
        )}

        {loading && (
          <div className="py-16 text-center">
            <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-gray-500 text-sm">Loading clients...</p>
          </div>
        )}

        {/* Table */}
        {!loading && (
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Client</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Contact</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">GSTIN</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden xl:table-cell">Jobs</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden xl:table-cell">Billed</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Portal Code</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((c, i) => (
                    <tr
                      key={c.id}
                      className={`border-b border-gray-50 hover:bg-gray-50 transition-colors ${i % 2 === 0 ? "" : "bg-gray-50/30"}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 text-sm font-bold flex-shrink-0">
                            {c.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{c.name}</p>
                            <p className="text-xs text-gray-500">{c.company}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-700">{c.email}</p>
                        <p className="text-xs text-gray-500">{c.phone}</p>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-xs font-mono text-gray-600 bg-gray-100 px-2 py-0.5 rounded">{c.gstin || "—"}</span>
                      </td>
                      <td className="px-4 py-3 text-center hidden xl:table-cell">
                        <div className="flex flex-col items-center">
                          <span className="text-sm font-bold text-gray-900">{getJobs(c)}</span>
                          {getActiveJobs(c) > 0 && (
                            <span className="text-xs text-amber-600">{getActiveJobs(c)} active</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right hidden xl:table-cell">
                        <span className="text-sm font-semibold text-gray-900">
                          ₹{(getBilled(c) / 100000).toFixed(1)}L
                        </span>
                      </td>

                      {/* ── Portal Access Code column ── */}
                      <td className="px-4 py-3 text-center hidden lg:table-cell">
                        {c.access_code ? (
                          <AccessCodeCell code={c.access_code} clientPhone={c.phone} clientName={c.name} />
                        ) : (
                          <span className="text-xs text-gray-400 italic">No code</span>
                        )}
                      </td>

                      <td className="px-4 py-3 text-center">
                        <span
                          className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${
                            c.status?.toLowerCase() === "active"
                              ? "bg-amber-50 text-amber-700"
                              : "bg-gray-100 text-gray-500"
                          }`}
                        >
                          {c.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => { setEditTarget(c); setModalOpen(true); }}
                            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 cursor-pointer transition-all"
                            title="Edit"
                          >
                            <i className="ri-edit-line text-sm" />
                          </button>
                          <button
                            onClick={() => handleDelete(c)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 cursor-pointer transition-all"
                            title="Deactivate"
                          >
                            <i className="ri-delete-bin-line text-sm" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {clients.length === 0 && (
              <div className="py-16 text-center">
                <span className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-search-line text-4xl text-gray-300" />
                </span>
                <p className="text-gray-500 text-sm">No clients found</p>
                <button
                  onClick={() => { setEditTarget(null); setModalOpen(true); }}
                  className="mt-3 text-xs text-amber-600 font-medium hover:underline cursor-pointer"
                >
                  Add your first client
                </button>
              </div>
            )}

            <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
              <span className="text-xs text-gray-500">
                Showing {clients.length} of {total} clients
              </span>
              {totalPages > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 cursor-pointer disabled:opacity-40"
                  >
                    <i className="ri-arrow-left-s-line text-sm" />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs font-semibold cursor-pointer ${
                        page === p ? "bg-amber-500 text-white" : "border border-gray-200 text-gray-500 hover:bg-gray-50"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 cursor-pointer disabled:opacity-40"
                  >
                    <i className="ri-arrow-right-s-line text-sm" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <BackToTop scrollContainerId="erp-main-scroll" />
    </ERPLayout>
  );
}

/** Inline access code cell with reveal-on-hover + copy + WhatsApp */
function AccessCodeCell({ code, clientPhone, clientName }: { code: string; clientPhone?: string; clientName?: string }) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(code).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleWhatsApp = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!clientPhone) return;
    const waSettings = getWhatsAppSettings();
    const url = buildWhatsAppUrl(clientPhone, clientName || 'Client', code, waSettings.template);
    window.open(url, '_blank');
  };

  return (
    <div className="flex items-center justify-center gap-1.5 group">
      <span
        className="font-mono text-xs font-bold text-gray-700 tracking-widest cursor-pointer select-all"
        onClick={() => setRevealed((r) => !r)}
        title="Click to reveal"
      >
        {revealed ? code : "••••••••"}
      </span>
      <button
        onClick={handleCopy}
        className={`w-5 h-5 flex items-center justify-center rounded transition-all cursor-pointer opacity-0 group-hover:opacity-100 ${
          copied ? "text-amber-600" : "text-gray-400 hover:text-amber-600"
        }`}
        title="Copy code"
      >
        <i className={`${copied ? "ri-checkbox-circle-line" : "ri-clipboard-line"} text-xs`} />
      </button>
      {clientPhone && (
        <button
          onClick={handleWhatsApp}
          className="w-5 h-5 flex items-center justify-center rounded transition-all cursor-pointer opacity-0 group-hover:opacity-100 text-gray-400 hover:text-green-600"
          title="Send via WhatsApp"
        >
          <i className="ri-whatsapp-line text-xs" />
        </button>
      )}
    </div>
  );
}
